// components/CurrentPayMethodChip.jsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";

/* ---------- Styles ---------- */
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f7f7f8;
  border: 1px solid #ececec;
  font-weight: 600;
  color: #333;
`;
const BrandBox = styled.div`
  width: 28px;
  height: 20px;
  border-radius: 4px;
  overflow: hidden;

  /* center the icon */
  display: flex;
  align-items: center;
  justify-content: center;

  /* kill baseline/whitespace issues on inline SVGs */
  svg {
    display: block;
    max-width: 100%;
    max-height: 100%;
    vertical-align: middle;
  }
`;

/* subtle skeleton while loading */
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;
const LoadingChip = styled(Chip)`
  width: 220px;
  .bar {
    height: 10px;
    width: 120px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eee 0%, #f6f6f6 50%, #eee 100%);
    background-size: 200px 100%;
    animation: ${shimmer} 1.2s infinite linear;
  }
`;

/* ---------- Small inline brand SVGs (no external assets) ---------- */
const VisaSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#1A4AB9" />
    <text
      x="4.5"
      y="13.5"
      fontFamily="Arial, sans-serif"
      fontWeight="700"
      fontSize="9"
      fill="#fff"
    >
      VISA
    </text>
  </svg>
);

const McSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#f3f3f3" />
    <circle cx="12" cy="10" r="6.2" fill="#EA001B" />
    <circle cx="16" cy="10" r="6.2" fill="#FF9900" opacity="0.9" />
  </svg>
);

const AmexSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#2E77BC" />
    <text
      x="3.8"
      y="13.2"
      fontFamily="Arial, sans-serif"
      fontWeight="700"
      fontSize="8"
      fill="#fff"
    >
      AMEX
    </text>
  </svg>
);

const DiscoverSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#ffffff" stroke="#ddd" />
    <circle cx="19" cy="10" r="6.5" fill="#FF7A00" />
  </svg>
);

const GenericCardSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#E9ECEF" />
    <rect x="5" y="6.5" width="18" height="3" fill="#C2C7CC" />
    <rect x="5" y="11.5" width="12" height="3" fill="#D5DADF" />
  </svg>
);

const BankSVG = () => (
  <svg width="28" height="20" viewBox="0 0 28 20">
    <rect width="28" height="20" rx="4" fill="#E9ECEF" />
    <polygon points="14,4 24,9 4,9" fill="#B7BDC4" />
    <rect x="6" y="9" width="16" height="6" fill="#C9CFD6" />
  </svg>
);

/* map Stripe brand -> SVG + pretty label */
function getBrandVisuals(brand, type = "card") {
  const b = (brand || "").toLowerCase();
  if (type === "sepa_debit") return { Icon: BankSVG, label: "SEPA" };
  if (b.includes("visa")) return { Icon: VisaSVG, label: "Visa" };
  if (b.includes("master")) return { Icon: McSVG, label: "Mastercard" };
  if (b.includes("american express") || b.includes("amex"))
    return { Icon: AmexSVG, label: "American Express" };
  if (b.includes("discover")) return { Icon: DiscoverSVG, label: "Discover" };
  return { Icon: GenericCardSVG, label: brand ? brand : "Cartão" };
}

/* utility */
const mask4 = (v) => (v ? `•••• ${v}` : "•••• ••••");

/* ---------- Component ---------- */
export default function CurrentPayMethodChip({ restaurantId }) {
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(false);
  const { userInfo } = useSelector((s) => s.userLogin || {});

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!restaurantId) return;
      if (!userInfo?.token) {
        toast.error("Tens de iniciar sessão para gerir o método de pagamento.");
        return;
      }
      setLoading(true);
      try {
        const r = await fetch(
          `/api/payments/payment-method-summary/${restaurantId}/`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
        );
        const data = await r.json();
        if (!cancelled) setPm(data?.payment_method || null);
      } catch (e) {
        if (!cancelled) setPm(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [restaurantId, userInfo?.token]);

  if (loading) {
    return (
      <LoadingChip aria-label="A carregar método de pagamento">
        <BrandBox>
          <GenericCardSVG />
        </BrandBox>
        <div className="bar" />
      </LoadingChip>
    );
  }

  if (!pm) {
    return <Chip>Não há método definido</Chip>;
  }

  if (pm.type === "card") {
    const { Icon, label } = getBrandVisuals(pm.brand, "card");
    return (
      <Chip title={`${label} •••• ${pm.last4}`}>
        <BrandBox>
          <Icon />
        </BrandBox>
        <span>
          {label} {mask4(pm.last4)}
        </span>
      </Chip>
    );
  }

  if (pm.type === "sepa_debit") {
    const { Icon, label } = getBrandVisuals(null, "sepa_debit");
    return (
      <Chip title={`${label} •••• ${pm.last4}`}>
        <BrandBox>
          <Icon />
        </BrandBox>
        <span>
          {label} {mask4(pm.last4)}
        </span>
      </Chip>
    );
  }

  // Fallback (unknown type)
  const { Icon, label } = getBrandVisuals(pm.brand || pm.type || "Método");
  return (
    <Chip>
      <BrandBox>
        <Icon />
      </BrandBox>
      <span>{label}</span>
    </Chip>
  );
}
